import React, { useMemo, useState, useEffect } from "react";
import { Box, Stack, Text } from "@mantine/core";
import { DisplayScheduleData, DisplayZone, apiTimeToFormTime } from "@/utils";
import { Event } from "@/utils/api";
import { DisplayBlockScheduleEventRow } from "../DisplayBlockScheduleEventRow";
import { ContentBlockArea } from "../ContentBlockArea";
import { BlockTransitionAnimationType } from "../BlockTransitionAnimationSelector";
import { ProgressStyleValue } from "../ProgressStyleSelector";

export interface DisplayBlockScheduleProps {
  schedule: DisplayScheduleData;

  /** Events to display in the schedule */
  events: Event[];

  /** Zone ID to filter events by (for multi-zone displays) */
  displayZoneId?: string;

  /** Display zone configuration (new system) */
  displayZone?: DisplayZone;

  /** Room name to display above the schedule (legacy fallback) */
  roomName?: string;

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;
}

const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  current_day: "Today",
  current_week: "This Week",
  current_month: "This Month",
  next_day: "Tomorrow",
  next_week: "Next Week",
  next_month: "Next Month",
  upcoming: "Upcoming Events",
};

/**
 * Parse a date string in YYYY-MM-DD format and return a Date object at midnight local time
 */
const parseEventDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  try {
    // Parse YYYY-MM-DD format
    const parts = dateString.split("-");
    if (parts.length !== 3) return null;

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    // Create date at midnight local time
    return new Date(year, month, day, 0, 0, 0, 0);
  } catch {
    return null;
  }
};

/**
 * Parse event time to minutes since midnight using apiTimeToFormTime helper
 * Returns minutes since midnight (e.g., 17:00 = 1020 minutes)
 */
const parseEventTimeToMinutes = (
  timeString: string | null | undefined,
): number | null => {
  if (!timeString) return null;

  try {
    // Use the helper to convert API time to HH:mm format
    const formTime = apiTimeToFormTime(timeString);

    if (!formTime) return null;

    // Parse HH:mm format to get hours and minutes
    const timeParts = formTime.split(":");
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);

      if (!isNaN(hours) && !isNaN(minutes)) {
        return hours * 60 + minutes;
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Get today's date at midnight for comparison
 */
const getTodayDate = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
};

/**
 * Check if two dates are the same day
 */
const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const DisplayBlockSchedule: React.FC<DisplayBlockScheduleProps> = ({
  schedule,
  events,
  displayZoneId,
  displayZone,
  roomName,
  className,
  themeConfig,
}) => {
  // Force re-filtering every minute by incrementing tick value
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Filter events by displayZoneId first (for multi-zone support)
  const zoneFilteredEvents = useMemo(() => {
    if (!events || events.length === 0) {
      return [];
    }

    // If no displayZoneId specified, return all events (backward compatible)
    if (!displayZoneId) {
      return events;
    }

    // Filter events that match this zone's displayZoneId
    return events.filter((event) => event.displayZoneId === displayZoneId);
  }, [events, displayZoneId]);

  // Filter events based on date and time parameters
  const currentAndUpcomingEvents = useMemo(() => {
    // If no events after zone filtering, return empty array immediately
    if (!zoneFilteredEvents || zoneFilteredEvents.length === 0) {
      return [];
    }

    const now = new Date();
    const today = getTodayDate();
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

    // Get display parameters with defaults
    const showPastHours = displayZone?.showPastHours ?? 0;
    const showUpcomingHours = displayZone?.showUpcomingHours ?? 24;

    // Convert hours to minutes for easier comparison
    const showPastMinutes = showPastHours * 60;
    const showUpcomingMinutes = showUpcomingHours * 60;

    return zoneFilteredEvents.filter((event) => {
      try {
        // Parse event date
        const eventDate = parseEventDate(event.eventDate);

        // If we can't parse the date, exclude the event for safety
        if (!eventDate) {
          return false;
        }

        // Check if event is today, in the past, or in the future
        const isToday = isSameDay(eventDate, today);
        const isInPast = eventDate < today;
        const isInFuture = eventDate > today;

        // Hide events from the past (before today)
        if (isInPast) {
          return false;
        }

        // For future dates (after today), show all events for that day
        // Time-based filtering only applies to today's events
        if (isInFuture) {
          return true;
        }

        // For today's events, apply time-based filtering
        if (isToday) {
          // Parse start and end times using helper from dateHelpers.ts
          const eventStartTime = parseEventTimeToMinutes(event.startTime);
          const eventEndTime = parseEventTimeToMinutes(event.endTime);

          // If we can't parse times for today's event, show it to be safe
          if (eventStartTime === null && eventEndTime === null) {
            return true;
          }

          // Check if event ended too long ago (past events filter)
          if (eventEndTime !== null) {
            const minutesSinceEventEnded = currentTimeInMinutes - eventEndTime;

            // Event ended more than showPastMinutes ago - don't show it
            if (minutesSinceEventEnded > showPastMinutes) {
              return false;
            }
          }

          // Check if event starts too far in the future (upcoming events filter)
          if (eventStartTime !== null) {
            const minutesUntilEventStarts =
              eventStartTime - currentTimeInMinutes;

            // Event starts more than showUpcomingMinutes from now - don't show it
            if (minutesUntilEventStarts > showUpcomingMinutes) {
              return false;
            }
          }

          return true;
        }

        // Should not reach here, but show event to be safe
        return true;
      } catch {
        // If we encounter any error, exclude the event for safety
        return false;
      }
    });
  }, [
    zoneFilteredEvents,
    tick,
    displayZone?.showPastHours,
    displayZone?.showUpcomingHours,
  ]);

  // Build header title based on zone filters
  const headerInfo = useMemo(() => {
    if (!displayZone) {
      // Legacy fallback
      return {
        title: roomName || "Events",
        showRoomInEvents: true,
        showFloorInEvents: true,
      };
    }

    const parts: string[] = [];
    let showRoomInEvents = true;
    let showFloorInEvents = true;

    if (displayZone.displayZoneTitle) {
      if (displayZone.displayZoneTitle !== "-") {
        parts.push(displayZone.displayZoneTitle);
      }
    } else {
      // Add schedule type (Today, Tomorrow, This Week, etc.)
      if (displayZone.scheduleType) {
        const scheduleLabel =
          SCHEDULE_TYPE_LABELS[displayZone.scheduleType] || "Events";
        parts.push(scheduleLabel);
      } else {
        parts.push("Events");
      }

      // Add location filters to title
      const locationParts: string[] = [];

      // Specific room (highest priority)
      if (displayZone.room?.name) {
        locationParts.push(displayZone.room.name);
        showRoomInEvents = false; // Don't show room in event tiles if it's in title

        // If room has floor, add it
        if (displayZone.room.floor) {
          locationParts.push(`Location ${displayZone.room.floor}`);
          showFloorInEvents = false; // Don't show floor in events if it's in title
        }
      }
      // Floor filter (without specific room)
      else if (displayZone.floor) {
        locationParts.push(`Location ${displayZone.floor}`);
        showFloorInEvents = false; // Don't show floor in events if it's in title
      }

      // Combine schedule type with location
      if (locationParts.length > 0) {
        return {
          title: parts[0], // Schedule type on first line
          subtitle: locationParts.join(" • "), // Location on second line
          showRoomInEvents,
          showFloorInEvents,
        };
      }
    }

    // No specific filters - showing all events
    return {
      title: parts[0] || "",
      subtitle: "",
      showRoomInEvents: true,
      showFloorInEvents: true,
    };
  }, [displayZone, roomName]);

  // Optional fallback playlist: shown in place of the "No events" message when
  // there are no current or upcoming events, so a schedule zone can play ads /
  // filler content while the room is idle.
  const fallbackPlayList = displayZone?.fallbackPlayList;
  const fallbackPlayListItems = fallbackPlayList?.playListItems ?? [];
  const hasFallbackPlayList = fallbackPlayListItems.length > 0;

  return (
    <Box
      className={`display-block-schedule ${className || ""}`}
      h="100%"
      style={{ overflow: "hidden" }}
      data-zone-id={displayZoneId}
      data-zone-events={zoneFilteredEvents.length}
      data-visible-events={currentAndUpcomingEvents.length}
    >
      <Stack gap="xs" h="100%">
        {/* Header with title and subtitle */}
        <Box className="display-block-schedule__header" pb="sm">
          {(headerInfo.title || headerInfo.subtitle) && (
            <Stack gap={4}>
              {headerInfo.title && (
                <Text
                  size="xl"
                  fw={600}
                  className="display-block-schedule__title"
                  ta="center"
                  style={{
                    textTransform: "uppercase",
                    color: "inherit",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                  }}
                >
                  {headerInfo.title}
                </Text>
              )}
              {headerInfo.subtitle && (
                <Text
                  size="xl"
                  fw={500}
                  className="display-block-schedule__subtitle"
                  ta="center"
                  style={{
                    color: "inherit",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    textTransform: "uppercase",
                    opacity: 0.9,
                  }}
                >
                  {headerInfo.subtitle}
                </Text>
              )}
            </Stack>
          )}
        </Box>

        {/* Events container */}
        <Box
          className="display-block-schedule__events-container"
          flex={1}
          style={{ overflow: "hidden" }}
        >
          {currentAndUpcomingEvents.length === 0 ? (
            hasFallbackPlayList ? (
              <ContentBlockArea
                className="display-block-schedule__fallback-playlist"
                playListItems={fallbackPlayListItems}
                zoneId={`${displayZone?.displayZoneId || displayZoneId || "zone"}-fallback`}
                venue={schedule?.venue}
                themeConfig={themeConfig}
                transitionType={
                  (fallbackPlayList?.blockTransitionAnimation ||
                    "none") as BlockTransitionAnimationType
                }
                progressStyle={
                  (fallbackPlayList?.progressStyle ||
                    "none") as ProgressStyleValue
                }
              />
            ) : (
              <Box
                className="display-block-schedule__no-events"
                h="100%"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  size="lg"
                  c="currentColor"
                  className="display-block-schedule__no-events-text"
                  ta="center"
                >
                  No events scheduled
                </Text>
              </Box>
            )
          ) : (
            <Stack gap={0} className="display-block-schedule__events-list">
              {currentAndUpcomingEvents.map((event) => (
                <DisplayBlockScheduleEventRow
                  key={event.id}
                  event={event}
                  displayMainRoom={headerInfo.showRoomInEvents}
                  displayFloor={headerInfo.showFloorInEvents}
                  mounted={true}
                  className="display-block-schedule__event-row"
                  themeConfig={themeConfig}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
